import axios from 'axios';
import {getConfig} from "../AppConfig";

interface DocumentBlobResponse {
    blob: Blob;
    filename: string;
    contentType: string;
}

interface DocumentBase64Response {
    filedata: string;
    fileext: string;
    filename: string;
    fileurl: string;
    id: string;
    serverModified: string | null;
    status?: string;
    message?: string;
}

/**
 * Fetches a document as a blob from the new blob-based API endpoint.
 * Falls back to base64 endpoint if blob endpoint fails.
 *
 * @param body - Request body containing document identifiers (id, woid, etc.)
 * @returns Promise resolving to an object URL for the document
 */
export async function fetchDocumentAsBlob(
    body: Record<string, any>,
    onProgress?: (bytesDownloaded: number, mbDownloaded: number) => void
): Promise<{
    blob: Blob;
}> {
    const FILENAME_REGEX = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    let blob: Blob | null = null;

    try {
        const response = await axios.post(
            '/api/documentsfileblob',
            body,
            {
                responseType: 'blob',
                onDownloadProgress: onProgress ? (progressEvent) => {
                    const mbDownloaded = progressEvent.loaded / (1024 * 1024);
                    onProgress(progressEvent.loaded, mbDownloaded);
                } : undefined
            }
        );

        blob = response.data;
        return { blob };
    }  catch (blobError) {
        console.error('Blob download failed:', blobError);
        blob = null;
        return null;
    }
}
/**
 * Fetches full timing data from server and logs combined metrics.
 */
async function fetchAndLogTiming(timingId: string, clientMs: number, fileId: string) {
    try {
        const timingResponse = await axios.get(`api/downloadtiming/${timingId}`);
        const t = timingResponse.data;
        console.log('[DOWNLOAD-TIMERS]', {
            client_total_ms: clientMs,
            server_total_ms: t.totalMs,
            server_sql_ms: t.sqlMs,
            server_metadata_ms: t.metadataMs,
            server_download_ms: t.downloadMs,
            infrastructure_gap_ms: clientMs - t.totalMs,
            fileId: fileId
        });
    } catch (e) {
        // Timing fetch failed, log what we have
        console.log('[DOWNLOAD-TIMERS] Could not fetch full timing', { client_total_ms: clientMs, fileId });
    }
}

/**
 * Fetches a document using the legacy base64 API endpoint.
 *
 * @param body - Request body containing document identifiers (id, woid, etc.)
 * @returns Promise resolving to an object URL for the document
 */
export async function fetchDocumentAsBase64(body: Record<string, any>): Promise<{
    url: string;
    filename: string;
    contentType: string;
}> {
    const response = await axios.post('api/documentsfile', body);
    const dataJson: DocumentBase64Response = JSON.parse(response.data.jsonString);

    if (dataJson?.status === 'failure') {
        throw new Error(dataJson?.message || 'Failed to fetch document');
    }

    const contentType = getContentType(dataJson.fileext);
    const byteCharacters = atob(dataJson.filedata);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: contentType });
    const url = window.URL.createObjectURL(blob);

    return { url, filename: dataJson.filename, contentType };
}

/**
 * Maps file extension to MIME content type
 */
function getContentType(fileext: string): string {
    const ext = fileext?.toLowerCase();
    switch (ext) {
        case 'png':
            return 'image/png';
        case 'pdf':
            return 'application/pdf';
        case 'jpeg':
        case 'jpg':
        case 'jfif':
            return 'image/jpeg';
        case 'docx':
            return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        case 'xlsx':
            return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        case 'xls':
            return 'application/vnd.ms-excel';
        default:
            return 'application/octet-stream';
    }
}